import gql from 'graphql-tag';
import { client } from '@src/apollo/client';
import { FetchPolicy } from 'apollo-boost';
import {
  goalNutrientsInPackFragment,
  positiveFeedbackFragment,
  topNutrientStudiesFragment,
} from '@queries/fragments';

const goalFragment = gql`
  fragment goal on HealthProfile_Goal {
    id
    name
    color
    healthPillar
    score
    selected
    description
    imageUrl
    tagline
    userPopularityLabel
    nutrientClaims {
      nutrientLabel
      claim
      amount
      unit
    }
  }
`;

export const pillFragment = gql`
  fragment pill on HealthProfile_Pill {
    id
    name
    imageUrl
    nutrients {
      name
      amount
      unit
      ingredient
      nrv
    }
  }
`;

const userDetailsFragment = gql`
  fragment userDetails on UserDetails {
    firstName
    lastName
    email
    phone
    dob
    customerDataPlatformId
    hasClaimedFreeTrial
  }
`;


export const SEND_MAGIC_LINK = gql`
  mutation user_sendMagicLink(
    $email: String!
    $destination: String
    $leadAttribution: [KeyValueInput]
  ) {
    user_sendMagicLink(email: $email, destination: $destination, leadAttribution: $leadAttribution)
  }
`;

export const REGISTER_USER = gql`
  mutation user_register($email: String!, $firstName: String!, $leadAttribution: [KeyValueInput]) {
    user_register(email: $email, firstName: $firstName, leadAttribution: $leadAttribution) {
      token
      customerDataPlatformId
    }
  }
`;

export const USER_DETAILS = gql`
  query user_details {
    user_details {
      ...userDetails
    }
  }
  ${userDetailsFragment}
`;


export const RECOMMENDED_PRODUCTS = gql`
  query recommended_products {
    health_recommendedProducts {
      id
      name
      updateAvailable
      subtype
      sku
      variant
      price
      currency
      isSubscribed
      pills {
        ...pill
      }
      action {
        value
      }
    }
  }
  ${pillFragment}
`;


export const GOALS = gql`
  query health_goals {
    health_goals {
      ...goal
      issues {
        id
        name
        imageUrl
      }
      expert {
        name
        imageUrl
        title
      }
    }
    user_details {
      firstName
    }
  }
  ${goalFragment}
`;

export const SELECT_GOALS = gql`
  mutation user_selectGoals($ids: [String]) {
    user_selectGoals(ids: $ids) {
      id
    }
  }
`;


export const CONTENT_SCREEN = gql`
  query content_getScreen($path: String!) {
    content_getScreen(path: $path) {
      title
      jsonStyle
      jsonSections
      canNavigateBack
    }
  }
`;

export const KIT_CAN_REGISTER = gql`
  query kit_canRegister($type: String!, $barcode: String!) {
    kit_canRegister(type: $type, barcode: $barcode)
  }
`;

export const REGISTER_KIT = gql`
  mutation user_registerKit(
    $type: String!
    $barcode: String!
    $firstName: String!
    $lastName: String!
    $biologicalSex: String!
    $phone: String!
    $dob: Int!
  ) {
    user_registerKit(
      type: $type
      barcode: $barcode
      firstName: $firstName
      lastName: $lastName
      biologicalSex: $biologicalSex
      phone: $phone
      dob: $dob
    )
  }
`;


export const USER_WARNING_SCREEN = gql`
  query user_warningScreen {
    user_warningScreen {
      title
      jsonStyle
      jsonSections
      canNavigateBack
      canDismiss
      onLoad {
        type
        value
      }
      onDismiss {
        type
        value
      }
    }
  }
`;


export const PROMOTE_GUEST_CONSULTATION = gql`
  mutation chat_promoteGuest($guestId: String!, $variant: String!) {
    chat_promoteGuest(guestId: $guestId, variant: $variant)
  }
`;


export const USER_SIGN_OUT = gql`
  mutation user_signOut {
    user_signOut
  }
`;

export const USER_AUTH_DETAILS = gql`
  query user_authDetails {
    user_authDetails {
      token
      customerDataPlatformId
    }
  }
`;


export const USER_KIT_STATUS = gql`
  query user_kitsStatus {
    user_kitsStatus {
      purchaseStatus {
        hasPurchasedDNAKit
        hasPurchasedBloodKit
      }
      kits {
        dna
        blood
      }
    }
  }
`;

export const getUserDetails = async (fetchPolicy: FetchPolicy = 'network-only') => {
  const { data: userDetailsData } = await client.query({
    query: USER_DETAILS,
    fetchPolicy,
  });
  return userDetailsData;
};

export const CONSULTATION_STATUS = gql`
  query user_consultationStatus {
    user_consultationStatus {
      status
    }
  }
`;

export const USER_CLAIMED_FREE_TRIALS = gql`
  query user_claimedFreeTrials {
    user_claimedFreeTrials {
      sku
      orderId
      purchaseDate
    }
  }
`;


export const HEALTH_ATTRIBUTES = gql`
  query health_attributes {
    health_attributes {
      id
      name
      score
      status
      value
    }
  }
`;

export const HEALTH_RESULTS = gql`
  query health_results($productType: String!) {
    health_results(productType: $productType) {
      ...positiveFeedback
      ...goalNutrientsInPack
      ...topNutrientStudies
    }
  }
  ${positiveFeedbackFragment}
  ${goalNutrientsInPackFragment}
  ${topNutrientStudiesFragment}
`;
